import React, { createContext, useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import themeConfig from '@/theme/theme.config';
import {
  getCustomStyles,
  getCustomColorSchemes,
  initCustomThemes,
  injectAllCustomCSS,
} from '@/theme/customThemes';
import {
  fetchAllThemes,
  getCachedThemes,
  saveThemePreference,
} from '@/api/themeApi';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

/* ── Helpers to build class-removal list dynamically ── */
function buildAllClasses(allStyles, allColorSchemes) {
  const cls = ['dark'];
  allStyles.forEach((s) => { if (s.id !== 'standard') cls.push(`style-${s.id}`); });
  allColorSchemes.forEach((c) => { if (c.id !== 'light') cls.push(`colors-${c.id}`); });
  return cls;
}

/**
 * Normalise backend theme records into the shape the rest of the system uses.
 * Backend returns { theme_type, theme_id, label, icon, description, is_dark, data }.
 * Styles  need: { id, label, icon, description, custom, vars }
 * Colors  need: { id, label, icon, isDark, custom, colors }
 */
function normaliseBackendThemes(records) {
  const styles = [];
  const colorSchemes = [];
  for (const r of records) {
    if (r.theme_type === 'style') {
      styles.push({
        id: r.theme_id, label: r.label, icon: r.icon,
        description: r.description || '', custom: true, vars: r.data || {},
      });
    } else {
      colorSchemes.push({
        id: r.theme_id, label: r.label, icon: r.icon,
        isDark: r.is_dark, custom: true, colors: r.data || {},
      });
    }
  }
  return { styles, colorSchemes };
}

export const ThemeProvider = ({ children }) => {
  /* ── Backend-synced theme lists ── */
  const [backendStyles, setBackendStyles] = useState([]);
  const [backendColors, setBackendColors] = useState([]);

  /* ── Old localStorage custom lists (kept for migration compat) ── */
  const [localStyles, setLocalStyles] = useState(getCustomStyles);
  const [localColors, setLocalColors] = useState(getCustomColorSchemes);

  /* ── Merge: built-in > backend > localStorage custom ── */
  const allStyles       = useMemo(() => [...themeConfig.styles, ...backendStyles, ...localStyles], [backendStyles, localStyles]);
  const allColorSchemes = useMemo(() => [...themeConfig.colorSchemes, ...backendColors, ...localColors], [backendColors, localColors]);

  const darkSet    = useMemo(() => new Set(allColorSchemes.filter((c) => c.isDark).map((c) => c.id)), [allColorSchemes]);
  const allClasses = useMemo(() => buildAllClasses(allStyles, allColorSchemes), [allStyles, allColorSchemes]);

  /* ── Inject localStorage custom CSS on mount ── */
  useEffect(() => { initCustomThemes(); }, []);

  /* ── Hydrate themes from backend ── */
  const hydratedWithTokenRef = useRef(false); // tracks whether we've hydrated with auth

  const hydrateFromServer = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return Promise.resolve(null);

    return fetchAllThemes()
      .then((fresh) => {
        const norm = normaliseBackendThemes([...fresh.platformThemes, ...fresh.personalThemes]);
        setBackendStyles(norm.styles);
        setBackendColors(norm.colorSchemes);

        // Apply server preference — always apply when hydrating from server
        // (the user hasn't made local changes in this session yet).
        if (fresh.preference) {
          if (fresh.preference.active_style) setStyleState(fresh.preference.active_style);
          if (fresh.preference.active_color_scheme) setColorSchemeState(fresh.preference.active_color_scheme);
          // Sync video background from server
          if (fresh.preference.background_video_url) {
            const serverVid = {
              url: fresh.preference.background_video_url,
              overlayOpacity: fresh.preference.background_video_overlay ?? 0.7,
              contentBgOpacity: fresh.preference.content_bg_opacity ?? 0.85,
            };
            setBackgroundVideoState(serverVid);
            localStorage.setItem('app-bg-video', JSON.stringify(serverVid));
          }
        }
        hydratedWithTokenRef.current = true;
        return fresh;
      })
      .catch(() => null);
  }, []);

  // On mount: use local cache for instant render, then try server
  useEffect(() => {
    const cached = getCachedThemes();
    const cachedNorm = normaliseBackendThemes([...cached.platformThemes, ...cached.personalThemes]);
    setBackendStyles(cachedNorm.styles);
    setBackendColors(cachedNorm.colorSchemes);
    hydrateFromServer();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // After login: the token appears in localStorage but ThemeProvider is already
  // mounted. Listen for the 'storage' event (cross-tab) and also poll briefly
  // so same-tab login is caught.
  useEffect(() => {
    // Cross-tab: 'storage' event fires when another tab writes to localStorage
    const onStorage = (e) => {
      if (e.key === 'token' && e.newValue && !hydratedWithTokenRef.current) {
        hydrateFromServer();
      }
    };
    window.addEventListener('storage', onStorage);

    // Same-tab: poll for token appearance for 30s after mount (covers login in same tab)
    const interval = setInterval(() => {
      if (hydratedWithTokenRef.current) {
        clearInterval(interval);
        return;
      }
      const token = localStorage.getItem('token');
      if (token) {
        hydrateFromServer();
        clearInterval(interval);
      }
    }, 1000);

    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, [hydrateFromServer]);

  /* ── State: two independent axes ── */
  const [style, setStyleState] = useState(() => {
    const saved = localStorage.getItem('app-style');
    return saved || 'standard';
  });

  const [colorScheme, setColorSchemeState] = useState(() => {
    const saved = localStorage.getItem('app-color-scheme');
    return saved || 'light';
  });

  /* ── Background video (per colour-scheme, optional) ── */
  const [backgroundVideo, setBackgroundVideoState] = useState(() => {
    try { return JSON.parse(localStorage.getItem('app-bg-video')) || null; }
    catch { return null; }
  });

  const isDark = darkSet.has(colorScheme);

  /* ── Refs to track latest values for debounced save ── */
  const latestStyleRef       = useRef(style);
  const latestColorRef       = useRef(colorScheme);
  const latestVideoRef       = useRef(backgroundVideo);
  latestStyleRef.current     = style;
  latestColorRef.current     = colorScheme;
  latestVideoRef.current     = backgroundVideo;

  /* ── Apply classes to <html> whenever either axis changes ── */
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove(...allClasses);

    if (style !== 'standard') root.classList.add(`style-${style}`);
    if (isDark) root.classList.add('dark');
    if (colorScheme !== 'light') root.classList.add(`colors-${colorScheme}`);

    localStorage.setItem('app-style', style);
    localStorage.setItem('app-color-scheme', colorScheme);
  }, [style, colorScheme, isDark, allClasses]);

  /* ── Sync content-bg-opacity CSS variable when video is active ── */
  useEffect(() => {
    const root = document.documentElement;
    const hasVideo = !!backgroundVideo?.url;
    const contentOpacity = backgroundVideo?.contentBgOpacity;
    if (hasVideo && contentOpacity != null && contentOpacity < 1) {
      root.style.setProperty('--content-bg-opacity', String(contentOpacity));
    } else {
      root.style.removeProperty('--content-bg-opacity');
    }
  }, [backgroundVideo]);

  /* ── Persist preference to backend (debounced) ── */
  const userChangedRef = useRef(false);
  const prefTimerRef = useRef(null);

  const scheduleSave = useCallback(() => {
    clearTimeout(prefTimerRef.current);
    prefTimerRef.current = setTimeout(() => {
      const token = localStorage.getItem('token');
      if (!token) return;
      const s = latestStyleRef.current;
      const c = latestColorRef.current;
      const v = latestVideoRef.current;
      console.debug('[ThemeContext] Saving preference →', { style: s, colorScheme: c, videoUrl: v?.url });
      saveThemePreference({
        activeStyle: s,
        activeColorScheme: c,
        backgroundVideoUrl: v?.url || '',
        backgroundVideoOverlay: v?.overlayOpacity ?? 0.7,
        contentBgOpacity: v?.contentBgOpacity ?? 0.85,
      });
    }, 600);
  }, []);

  /* ── Setters — explicitly trigger backend save on user interaction ── */
  const setStyle = useCallback((id) => {
    userChangedRef.current = true;
    setStyleState(id);
    // Update ref immediately so scheduleSave reads the latest value
    latestStyleRef.current = id;
    scheduleSave();
  }, [scheduleSave]);

  const setColorScheme = useCallback((id) => {
    userChangedRef.current = true;
    setColorSchemeState(id);
    latestColorRef.current = id;
    scheduleSave();
  }, [scheduleSave]);

  const setBackgroundVideo = useCallback((video) => {
    userChangedRef.current = true;
    setBackgroundVideoState(video);
    latestVideoRef.current = video;
    if (video && video.url) {
      localStorage.setItem('app-bg-video', JSON.stringify(video));
    } else {
      localStorage.removeItem('app-bg-video');
    }
    scheduleSave();
  }, [scheduleSave]);

  /* ── Refresh: re-read localStorage custom + re-fetch backend ── */
  const refreshCustomThemes = useCallback(() => {
    // LocalStorage customs
    setLocalStyles(getCustomStyles());
    setLocalColors(getCustomColorSchemes());
    injectAllCustomCSS();

    // Backend themes
    const token = localStorage.getItem('token');
    if (token) {
      fetchAllThemes().then((fresh) => {
        const norm = normaliseBackendThemes([...fresh.platformThemes, ...fresh.personalThemes]);
        setBackendStyles(norm.styles);
        setBackendColors(norm.colorSchemes);
      }).catch(() => {});
    }
  }, []);

  /* ── Quick toggle: swap light ↔ dark ── */
  const toggleDarkMode = useCallback(() => {
    setColorSchemeState((prev) => {
      let next;
      if (prev === 'light')         next = 'dark';
      else if (prev === 'dark')     next = 'light';
      else if (prev === 'ai-light') next = 'ai-dark';
      else if (prev === 'ai-dark')  next = 'ai-light';
      else                          next = 'dark';
      userChangedRef.current = true;
      latestColorRef.current = next;
      scheduleSave();
      return next;
    });
  }, [scheduleSave]);

  /* ── Colour lookup ── */
  const colors = useMemo(() => {
    if (themeConfig.colors[colorScheme]) return themeConfig.colors[colorScheme];
    const custom = [...backendColors, ...localColors].find((c) => c.id === colorScheme);
    return custom?.colors || themeConfig.colors.light;
  }, [colorScheme, backendColors, localColors]);

  const theme = style === 'standard' ? colorScheme : `${style}-${colorScheme}`;

  const value = useMemo(
    () => ({
      style, setStyle,
      colorScheme, setColorScheme,
      toggleDarkMode, isDark, colors,
      styles: allStyles,
      colorSchemes: allColorSchemes,
      refreshCustomThemes,
      hydrateFromServer,
      theme,
      themes: themeConfig.themes,
      backgroundVideo, setBackgroundVideo,
    }),
    [style, setStyle, colorScheme, setColorScheme, toggleDarkMode, isDark, colors, allStyles, allColorSchemes, refreshCustomThemes, hydrateFromServer, theme, backgroundVideo, setBackgroundVideo],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
